// The Fleet health page's charts (WEB-DBG-FLT-03, DBG-52) from vision-api's FleetHealth rows: one line per product or
// firmware version, per day. Pure, so it is tested without drawing.
import type { components } from "../../../api/generated/vision-api";
import type { ChartLine } from "../../../widgets/charts/option";

export type FleetHealth = components["schemas"]["FleetHealth"];
type Row = FleetHealth["rows"][number];

export const HEALTH_RANGES = ["7d", "30d", "90d"] as const;
export type HealthRange = (typeof HEALTH_RANGES)[number];
export type HealthGroup = "product" | "fw_version";
export const HEALTH_METRICS = ["frames", "bytes", "seal_failures", "watchdog_resets", "reconnects"] as const;
export type HealthMetric = (typeof HEALTH_METRICS)[number];

const DAYS: Record<HealthRange, number> = { "7d": 7, "30d": 30, "90d": 90 };

/** The asked number of days, today (UTC) the last of them; the route takes dates, both bounds included. */
export function healthWindow(range: HealthRange, now: Date): { from: string; to: string } {
  const date = (d: Date) => d.toISOString().slice(0, 10);
  return { from: date(new Date(now.getTime() - (DAYS[range] - 1) * 86_400_000)), to: date(now) };
}

const day = (d: string) => Date.parse(`${d.slice(0, 10)}T00:00:00Z`);

/**
 * One chart's lines: every group a line over the same days, a day the group has no row for a gap. Bytes are drawn in
 * megabytes. The groups with the most of the metric come first, and past eight the rest are summed as "Other" so the
 * colours stay apart.
 */
export function healthLines(rows: FleetHealth["rows"], metric: HealthMetric, other: string): ChartLine[] {
  const days = [...new Set(rows.map((r) => day(r.day)).filter((t) => !Number.isNaN(t)))].sort((a, b) => a - b);
  const byGroup = new Map<string, Map<number, Row>>();
  for (const r of rows) {
    const t = day(r.day);
    if (Number.isNaN(t)) continue;
    const g = byGroup.get(r.group) ?? new Map<number, Row>();
    g.set(t, r);
    byGroup.set(r.group, g);
  }
  const scale = metric === "bytes" ? 1e-6 : 1;
  const series = [...byGroup].map(([group, perDay]) => ({
    group,
    values: days.map((t) => {
      const v = perDay.get(t)?.[metric];
      return typeof v === "number" ? v * scale : null;
    }),
  }));
  const total = (vs: (number | null)[]) => vs.reduce<number>((a, v) => a + (v ?? 0), 0);
  series.sort((a, b) => total(b.values) - total(a.values) || a.group.localeCompare(b.group));
  const shown = series.length > 8 ? series.slice(0, 7) : series;
  const rest = series.length > 8 ? series.slice(7) : [];
  const lines = shown.map((s) => ({ name: s.group, values: s.values }));
  if (rest.length) {
    lines.push({
      name: other,
      values: days.map((_, i) => {
        const vs = rest.map((s) => s.values[i]).filter((v): v is number => v != null);
        return vs.length ? vs.reduce((a, v) => a + v, 0) : null;
      }),
    });
  }
  return lines.map((l) => ({
    name: l.name,
    unit: metric === "bytes" ? "MB" : null,
    decimals: metric === "bytes" ? 1 : 0,
    series: { times: days, values: l.values, quality: [] },
  }));
}
